import { acknowledgementInfo, ANIMATION_DURATION } from '@/constants/register.contants'
import { useRegister } from '@/hooks/Landing/useRegister'
import { Button, Grow, List, ListItem, Paper, Typography } from '@mui/material'
import { IconArrowUp } from '@tabler/icons-react'
import React, { useState } from 'react'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'

export const ConsentForm: React.FC<ConsentFormProps> = ({ interests, onFormSubmit }) => {
  const [ackErrorMessage, setAckErrorMessage] = useState('')
  const [acknowledgements, setAcknowledgements] = useState<Interest[]>(acknowledgementInfo)

  const { setShowError, ackInfo, setAckInfo, additionalThoughts, setSelectedInterests } = useRegister()

  const handleAcknowledgementSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target

    setAcknowledgements((prev) =>
      prev.map((item) => (item.id === value ? { ...item, selected: !item.selected } : item)),
    )

    setAckInfo({
      ...ackInfo,
      [value]: checked,
    })
  }

  const validateForm = () => {
    let valid = true

    if (Object.values(ackInfo).some((value) => !value)) {
      setAckErrorMessage('Please acknowledge all statements.')
      valid = false
    } else {
      setAckErrorMessage('')
    }

    return valid
  }

  const handleSubmit = () => {
    if (!validateForm()) return

    setAckErrorMessage('')
    setShowError(false)

    const selectedInterests: string[] = interests
      .filter((interest) => interest.selected)
      .map((interest) => interest.label)
    setSelectedInterests(selectedInterests)

    onFormSubmit(selectedInterests, additionalThoughts, ackInfo)
  }

  const replaceWithLink = (text: string, word: string, href: string) => {
    if (!text.includes(word)) return text
    const parts = text.split(word)
    return (
      <>
        {parts[0]}
        <a href={href} target="_blank" rel=" noopener noreferrer">
          <Typography variant="body2" color="warning">
            {word}
          </Typography>
        </a>
        {parts[1]}
      </>
    )
  }

  return (
    <Grow in={true} timeout={ANIMATION_DURATION}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          width: '100%',
          position: 'relative',
        }}
      >
        <List sx={{ mb: 2 }} dense disablePadding>
          {acknowledgements.map((acknowledgement) => (
            <ListItem
              key={acknowledgement.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                py: 1,
                my: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledgement.selected}
                    value={acknowledgement.id}
                    onChange={handleAcknowledgementSelection}
                  />
                }
                label={replaceWithLink(
                  acknowledgement.label,
                  'Terms of Use | Asian Development Bank',
                  'https://www.adb.org/terms-use',
                )}
              />
            </ListItem>
          ))}
        </List>
        {ackErrorMessage && (
          <Typography variant="body2" color="error" sx={{ mt: 1, ml: 2, mb: 1 }}>
            {ackErrorMessage}
          </Typography>
        )}

        <Button
          variant="outlined"
          onClick={handleSubmit}
          sx={{
            position: 'absolute',
            right: 10,
            bottom: 10,
            borderRadius: 12,
            minWidth: 35,
            borderWidth: 2,
            p: 1,
          }}
        >
          <IconArrowUp size={18} stroke={4} />
        </Button>
      </Paper>
    </Grow>
  )
}
